import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getTokenFromStorage } from '@/utils/auth';
import {
  getCampaignsSearch,
  type ClientCampaignsSearchResponse,
  type ClientCampaignSortBy,
  type GetCampaignsSearchParams,
} from '@/api/beatlyApi';

/** Filters supported server-side (beatly-api-v2 GET .../campaigns/search). */
export type CampaignsListFilters = Pick<
  GetCampaignsSearchParams,
  'text' | 'brandName' | 'brandId' | 'listStatus' | 'sortBy' | 'platform' | 'country'
>;

export interface UseCampaignsParams {
  pageNumber?: number;
  pageSize?: number;
  /**
   * Client team id (must match a team the user belongs to). Same as Settings / `user.team`.
   * Omit or empty string disables the query until a team id is known.
   */
  teamScope?: string;
  /** Pass-through to API; omit fields for no filter. */
  filters?: CampaignsListFilters;
}

function filtersKey(f: CampaignsListFilters | undefined): string {
  if (!f || Object.keys(f).length === 0) {
    return '';
  }
  return JSON.stringify({
    text: f.text ?? '',
    brandName: f.brandName ?? '',
    brandId: f.brandId ?? '',
    listStatus: f.listStatus ?? '',
    sortBy: f.sortBy ?? 'newest',
    platform: f.platform ?? '',
    country: f.country ?? '',
  });
}

export interface CampaignsListQueryArgs {
  teamScope: string;
  pageNumber: number;
  pageSize: number;
  filters: CampaignsListFilters;
}

/** Shared query-key factory so prefetch and `useCampaigns` can never drift. */
export function buildCampaignsListQueryKey(args: CampaignsListQueryArgs): readonly unknown[] {
  const { teamScope, pageNumber, pageSize, filters } = args;
  return [
    'client',
    'campaigns',
    'search',
    teamScope,
    pageNumber,
    pageSize,
    filtersKey(filters),
  ];
}

/** Shared fetcher for the campaigns list (used by `useCampaigns` and prefetch). */
export function fetchCampaignsList(
  args: CampaignsListQueryArgs,
  token: string,
): Promise<ClientCampaignsSearchResponse> {
  const { teamScope, pageNumber, pageSize, filters } = args;
  const sortBy: ClientCampaignSortBy = filters.sortBy ?? 'newest';
  return getCampaignsSearch(
    {
      pageNumber,
      pageSize,
      teamId: teamScope,
      text: filters.text,
      brandName: filters.brandName,
      brandId: filters.brandId,
      listStatus: filters.listStatus,
      sortBy,
      platform: filters.platform,
      country: filters.country,
    },
    token,
  );
}

/**
 * Fetches paginated campaigns for the current client.
 * Same API as beatly-web-app: GET /app/v2/client/campaigns/search
 */
export function useCampaigns(params: UseCampaignsParams = {}) {
  const { pageNumber = 0, pageSize = 10, teamScope = '', filters = {} } = params;
  const { user } = useAuth();
  const token = getTokenFromStorage();

  const specificTeamId = teamScope.trim();

  const enabled = Boolean(token && user?.is_client && specificTeamId);

  const queryArgs = useMemo<CampaignsListQueryArgs>(
    () => ({
      teamScope: specificTeamId,
      pageNumber,
      pageSize,
      filters: {
        text: filters.text,
        brandName: filters.brandName,
        brandId: filters.brandId,
        listStatus: filters.listStatus,
        sortBy: filters.sortBy ?? 'newest',
        platform: filters.platform,
        country: filters.country,
      },
    }),
    [
      specificTeamId,
      pageNumber,
      pageSize,
      filters.text,
      filters.brandName,
      filters.brandId,
      filters.listStatus,
      filters.sortBy,
      filters.platform,
      filters.country,
    ],
  );

  const { data, isLoading, error, isError } = useQuery({
    queryKey: buildCampaignsListQueryKey(queryArgs),
    queryFn: async (): Promise<ClientCampaignsSearchResponse> =>
      fetchCampaignsList(queryArgs, token ?? ''),
    enabled,
  });

  return {
    data: data ?? {
      campaigns: [],
      page_number: 0,
      page_size: pageSize,
      count_total: 0,
      has_more: false,
    },
    isLoading,
    error,
    isError,
    isEnabled: enabled,
  };
}
